// Types, niveaux, thématiques et outils partagés par les pages Cours, thématique, glossaire, lecture et l'admin
import {
  BookOpen, Brain, Building2, Calculator, ChartLine, Coins, Globe, Landmark, Layers, Percent,
  PiggyBank, Scale, Shield, Target, TrendingUp, Zap,
} from "lucide-react";
export const COURSE_TYPES = [
  { id: "article", label: "Rétrospective" },
  { id: "concept", label: "Concept" },
  { id: "vocabulary", label: "Glossaire" },
];
export const COURSE_TYPE_LABEL = {
  article: "Rétrospective",
  concept: "Concept",
  vocabulary: "Glossaire",
  video: "Vidéo",
  news: "Actualité",
};
export const COURSE_LEVELS = [
  { id: "debutant", label: "Débutant", n: 1 },
  { id: "intermediaire", label: "Intermédiaire", n: 2 },
  { id: "avance", label: "Avancé", n: 3 },
];
export const levelMeta = (id) => COURSE_LEVELS.find((l) => l.id === id) || null;
// Icônes proposées pour les thématiques (clé enregistrée en base → composant)
export const THEME_ICONS = {
  layers: Layers, globe: Globe, calculator: Calculator, chart: ChartLine, shield: Shield, coins: Coins,
  brain: Brain, book: BookOpen, landmark: Landmark, building: Building2, piggy: PiggyBank, trending: TrendingUp,
  scale: Scale, target: Target, percent: Percent, zap: Zap,
};
export const themeIcon = (key) => THEME_ICONS[key] || Layers;
// « Autres concepts » : dossier automatique pour les concepts publiés sans thématique
export const OTHER_THEME_SLUG = "autres-concepts";
/** Ne garde que les contenus publics de la page Cours (pas de vidéo / actualité, pas de brouillon). */
export const isLearningContent = (c) =>
  c && c.published !== false && ["concept", "article", "vocabulary"].includes(c.category);
/**
 * Thématiques prêtes à afficher : leurs cours publiés dans l'ordre choisi, puis « Autres concepts »
 * (concepts sans thématique), seulement s'il en reste.
 */
export function buildThemes(themes = [], courses = []) {
  const byId = Object.fromEntries(courses.map((c) => [c.id, c]));
  const out = themes.map((t) => ({
    ...t,
    courses: (t.course_ids || []).map((id) => byId[id]).filter((c) => c && c.category === "concept" && c.published !== false),
  }));
  const placed = new Set(out.flatMap((t) => t.courses.map((c) => c.id)));
  const loose = courses
    .filter((c) => c.category === "concept" && c.published !== false && !placed.has(c.id))
    .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  if (loose.length) {
    out.push({
      id: "autres",
      slug: OTHER_THEME_SLUG,
      title: "Autres concepts",
      description: "Des concepts qui ne sont pas encore rangés dans une thématique.",
      icon: "book",
      courses: loose,
      isOther: true,
    });
  }
  return out;
}
/** Thématique d'un concept (dans la liste construite par buildThemes). */
export const themeOfCourse = (builtThemes, course) =>
  builtThemes.find((t) => t.courses.some((c) => c.id === course?.id)) || null;
// ── Rétrospectives : dates ──
// Une année est un nombre : négatif avant J.-C. (-1750), positif après (1, 33, 2008). Il n'y a pas d'année 0.
// Le serveur fournit year_start / year_end (et period, le texte déjà mis en forme).
/** Année de début d'une rétrospective (pour la frise) ; ancien texte libre lu au mieux ; sans date → à la fin. */
export const retroYear = (c) => {
  if (typeof c?.year_start === "number") return c.year_start;
  const t = String(c?.period || "");
  const m = t.match(/\d{1,5}/);
  if (!m) return Number.POSITIVE_INFINITY;
  return /\bav/i.test(t.slice(m.index)) ? -parseInt(m[0], 10) : parseInt(m[0], 10);
};
export const sortRetros = (list) =>
  [...list].sort((a, b) => retroYear(a) - retroYear(b) || (a.title || "").localeCompare(b.title || "", "fr"));
/**
 * Date en deux morceaux pour l'affichage : { main: "1750", suffix: "av. J.-C." }.
 * « 2008 », « 2007–2008 », « 1750 av. J.-C. », « 220–206 av. J.-C. », « 33 apr. J.-C. », « 50 av. – 20 apr. J.-C. ».
 * Avant l'an 1000, on précise « apr. J.-C. » pour éviter toute confusion.
 */
export function periodParts(start, end) {
  if (typeof start !== "number") return null;
  if (typeof end !== "number" || end === start) {
    if (start < 0) return { main: String(-start), suffix: "av. J.-C." };
    return { main: String(start), suffix: start < 1000 ? "apr. J.-C." : "" };
  }
  if (start < 0 && end < 0) return { main: `${-start}–${-end}`, suffix: "av. J.-C." };
  if (start < 0) return { main: `${-start} av. – ${end}`, suffix: "apr. J.-C." };
  return { main: `${start}–${end}`, suffix: end < 1000 ? "apr. J.-C." : "" };
}
/** Même chose pour un cours : { main, suffix } (ancien texte libre : tout dans main). */
export const retroDateParts = (c) =>
  periodParts(c?.year_start, c?.year_end) || { main: c?.period || "", suffix: "" };
/** Texte complet : « 1750 av. J.-C. ». */
export const periodText = (start, end) => {
  const p = periodParts(start, end);
  return p ? [p.main, p.suffix].filter(Boolean).join(" ") : "";
};
/**
 * Contenus liés à un cours : ses liens choisis à la main, puis ceux des autres contenus
 * qui pointent vers lui avec un lien réciproque.
 */
export function relatedOf(course, courses = []) {
  if (!course) return [];
  const byId = Object.fromEntries(courses.map((c) => [c.id, c]));
  const ids = (course.links || []).map((l) => l.id);
  courses.forEach((c) => {
    if (c.id !== course.id && (c.links || []).some((l) => l.id === course.id && l.recip)) ids.push(c.id);
  });
  return [...new Set(ids)].map((id) => byId[id]).filter((c) => c && c.id !== course.id && isLearningContent(c));
}
// Glossaire
export const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
export const normalize = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
export const firstLetter = (s) => normalize((s || "").trim()).charAt(0).toUpperCase();
export const sortAlpha = (list) => [...list].sort((a, b) => (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" }));
